import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { DevolutionChartModel } from 'src/app/models/charts/rental/devolution-chart.model';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { DevolutionPrintModel } from 'src/app/models/print/rental/devolution-print.model';
import { DevolutionModel } from 'src/app/models/rental/devolution.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoiceDevolutionTableModel } from 'src/app/models/tables/rental/invoice-devolution-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DevolutionService {

  constructor(private _httpClient: HttpClient) { }

  annulDevolution(devolutionId: number): Observable<DevolutionModel> {
    return this._httpClient.put<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.AnnulDevolution}/${devolutionId}`,
      null);
  }

  attachFilesToDevolution(devolutionId: number, files: FormData): Observable<Array<string>> {
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.AttachFilesToDevolution}/${devolutionId}`, files);
  }

  blockDevolution(devolutionId: number): Observable<DevolutionModel> {
    return this._httpClient.put<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.BlockDevolution}/${devolutionId}`,
      null);
  }

  createDevolution(devolutionContract: DevolutionModel): Observable<DevolutionModel> {
    return this._httpClient.post<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.CreateDevolution}`,
      devolutionContract);
  }

  deleteDevolution(devolutionId: number): Observable<DevolutionModel> {
    return this._httpClient.delete<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.DeleteDevolution}/${devolutionId}`);
  }

  deleteFilesToDevolution(devolutionId: number, file: string): Observable<Array<string>> {
    const itemFilterResponse = new ItemFilterModel("file", null, devolutionId, file);
    return this._httpClient.post<Array<string>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.DeleteFilesToDevolution}`, itemFilterResponse);
  }

  downloadFileToDevolution(devolutionId: number, file: string): Observable<HttpEvent<Blob>> {
    const itemFilterResponse = new ItemFilterModel("file", null, devolutionId, file);
    return this._httpClient.request(new HttpRequest('POST', `${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.DownloadFileToDevolution}`,
      itemFilterResponse, { reportProgress: true, responseType: 'blob' }));
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean, timeZoneId: string): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString()).set('timeZoneId', timeZoneId);
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.ExportGeneralConsultation}`, filters, {
      params, responseType: 'blob',
    });
  }

  exportInvoiceForDevolutionConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());

    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.ExportInvoiceForDevolutionConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getFilesToDevolution(devolutionId: number): Observable<any[]> {
    return this._httpClient.get<any[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetFilesToDevolution}/${devolutionId}`);
  }

  getAllCostCentersForSelectAsync(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetAllCostCentersForSelectAsync}`,
      { params: params });
  }

  getAllDevolutionsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetAllDevolutionsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllInvoiceForDevolutionsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<InvoiceDevolutionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<InvoiceDevolutionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetAllInvoiceForDevolutionsWithPaginationAsync}`,
      filters, { params: params }
    );
  }

  getAllTransportersForSelectAsync(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetAllTransportersForSelectAsync}`,
      { params: params });
  }

  getAllVehiclesForSelectAsync(branchId: number): Observable<Array<any>> {
    const params = new HttpParams().set('branchId', branchId)
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetAllVehiclesForSelectAsync}`,
      { params: params });
  }

  getAllConceptsRequirementTypeForSelect(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetAllConceptsRequirementTypeForSelectAsync}`);
  }

  getAllConceptsDelayReasonForSelect(): Observable<Array<any>> {
    return this._httpClient.get<Array<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetAllConceptsDelayReasonForSelectAsync}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetColumnsForGeneralConsultationAsync}`);
  }

  getColumnsForInvoiceConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetColumnsForInvoiceConsultationAsync}`);
  }

  getCostCenterByIdAsync(costCenterId: number): Observable<any> {
    return this._httpClient.get<any>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetCostCenterByIdAsync}/${costCenterId}`);
  }

  getDevolutionById(devolutionId: number): Observable<DevolutionModel> {
    return this._httpClient.get<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetDevolutionById}/${devolutionId}`);
  }

  getDevolutionByKeysAsync(branchId: number, consecutive: number): Observable<DevolutionModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetDevolutionByKeys}`, { params: params });
  }

  getDevolutionByKeysByPrintAsync(branchId: number, consecutive: number, devolutionId: number, isPredevolution: boolean,
    swIncludeStore: boolean, printOnlyQuantity: boolean): Observable<DevolutionPrintModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive).set('devolutionId', devolutionId)
      .set('isPredevolution', isPredevolution).set('swIncludeStore', swIncludeStore).set('printOnlyQuantity', printOnlyQuantity);
    return this._httpClient.get<DevolutionPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetDevolutionByKeysByPrintAsync}`,
      { params }
    );
  }

  getDevolutionConsecutiveByIdAsync(devolutionId: number): Observable<number> {
    return this._httpClient.get<{ value: number }>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetDevolutionConsecutiveByIdAsync}/${devolutionId}`)
      .pipe(map(response => response.value));
  }

  getEmptyDevolutionPrint(): Observable<DevolutionPrintModel> {
    return this._httpClient.get<DevolutionPrintModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetEmptyDevolutionPrint}`);
  }

  getFirstDevolutionByKeysAsync(branchId: number): Observable<DevolutionModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetFirstDevolutionByKeys}`, { params: params });
  }

  getIndicatorsForGeneralConsultation(filters: ColumnResponseModel[]): Observable<DevolutionChartModel> {
    return this._httpClient.post<DevolutionChartModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetIndicatorsForGeneralConsultationAsync}`,
      filters
    );
  }

  getLastDevolutionByKeysAsync(branchId: number): Observable<DevolutionModel> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetLastDevolutionByKeys}`, { params: params });
  }

  getNextDevolutionByKeysAsync(branchId: number, consecutive: number): Observable<DevolutionModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetNextDevolutionByKeys}`, { params: params });
  }

  getPermissionPrint(): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetPermissionPrint}`);
  }

  getPreviusDevolutionByKeysAsync(branchId: number, consecutive: number): Observable<DevolutionModel> {
    const params = new HttpParams().set('branchId', branchId).set('consecutive', consecutive);
    return this._httpClient.get<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetPreviusDevolutionByKeys}`, { params: params });
  }

  getPredevolutionByKeysAsync(itemFilters: Array<ItemFilterModel>): Observable<DevolutionModel> {
    return this._httpClient.post<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.GetPredevolutionByKeys}`,
      itemFilters);
  }

  hasExistingDevolution(branchId: number): Observable<boolean> {
    const params = new HttpParams().set('branchId', branchId);
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.HasExistingDevolutionAsync}`,
      { params: params }
    );
  }

  printDevolution(devolutionId: number): Observable<DevolutionModel> {
    return this._httpClient.put<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.PrintDevolution}/${devolutionId}`,
      null);
  }

  setDevolutionState(devolutionId: number): Observable<DevolutionModel> {
    return this._httpClient.put<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.SetDevolutionState}/${devolutionId}`,
      null);
  }

  updateDevolution(devolutionContract: DevolutionModel): Observable<DevolutionModel> {
    return this._httpClient.put<DevolutionModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.UpdateDevolution}`,
      devolutionContract);
  }

  validateStocksInCostCenter(branchId: number, costCenterId: number): Observable<boolean> {
    return this._httpClient.get<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.devolution.validateStocksInCostCenter}/${branchId}/${costCenterId}`);
  }
}
